export interface EchartsTooltipRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EchartsTooltipSize {
  viewSize: number[];
  contentSize: number[];
}

export interface EchartsRadialTooltipPositionOptions {
  center?: readonly [number, number];
  clamp?: boolean | 'x' | 'y';
  gap?: number;
  radius: number;
}

export interface EchartsFrozenTooltipPositionOptions<TParams, TRect> {
  getKey: (params: TParams) => string;
  getPosition: (
    point: number[],
    params: TParams,
    rect: TRect,
    size: EchartsTooltipSize,
  ) => number[];
}

export function createFrozenEchartsTooltipPosition<
  TParams = unknown,
  TRect = unknown,
>({
  getKey,
  getPosition,
}: EchartsFrozenTooltipPositionOptions<TParams, TRect>) {
  let frozenAnchor: { key: string; position: number[] } | null = null;

  return (
    point: number[],
    params: unknown,
    _dom: unknown,
    rect: unknown,
    size: EchartsTooltipSize,
  ): number[] => {
    const typedParams = params as TParams;
    const typedRect = rect as TRect;
    const key = getKey(typedParams);

    if (frozenAnchor?.key === key) {
      return frozenAnchor.position;
    }

    const position = getPosition(point, typedParams, typedRect, size);
    frozenAnchor = { key, position };

    return position;
  };
}

export function getHorizontalBarTooltipPosition(
  point: number[],
  rect: EchartsTooltipRect | null,
  size: EchartsTooltipSize,
): number[] {
  const gap = 6;
  const [tooltipWidth, tooltipHeight] = size.contentSize;
  const [viewWidth, viewHeight] = size.viewSize;

  if (!rect) {
    const fallbackY = point[1] - tooltipHeight - gap;

    return [
      Math.max(0, Math.min(point[0], viewWidth - tooltipWidth)),
      Math.max(0, fallbackY),
    ];
  }

  const x = Math.max(
    0,
    Math.min(
      rect.x + rect.width / 2 - tooltipWidth / 2,
      viewWidth - tooltipWidth,
    ),
  );
  const yAbove = rect.y - tooltipHeight - gap;
  const y = yAbove >= 0 ? yAbove : rect.y + rect.height + gap;

  return [x, Math.max(0, Math.min(y, viewHeight - tooltipHeight))];
}

export function getRadialTooltipPosition(
  point: number[],
  size: EchartsTooltipSize,
  {
    center = [0.5, 0.5],
    clamp = true,
    gap = 16,
    radius,
  }: EchartsRadialTooltipPositionOptions,
): number[] {
  const [viewWidth, viewHeight] = size.viewSize;
  const [tooltipWidth, tooltipHeight] = size.contentSize;
  const centerX = viewWidth * center[0];
  const centerY = viewHeight * center[1];
  const radialDistance = Math.min(viewWidth, viewHeight) * radius;
  const distance = Math.hypot(point[0] - centerX, point[1] - centerY) || 1;
  const unitX = (point[0] - centerX) / distance;
  const unitY = (point[1] - centerY) / distance;
  const anchorX = centerX + unitX * (radialDistance + gap);
  const anchorY = centerY + unitY * (radialDistance + gap);
  const left = unitX < 0 ? anchorX - tooltipWidth : anchorX;
  const top = unitY < 0 ? anchorY - tooltipHeight : anchorY;

  if (!clamp) {
    return [left, top];
  }

  if (clamp === 'x') {
    return [Math.max(0, Math.min(left, viewWidth - tooltipWidth)), top];
  }

  if (clamp === 'y') {
    return [left, Math.max(0, Math.min(top, viewHeight - tooltipHeight))];
  }

  return [
    Math.max(0, Math.min(left, viewWidth - tooltipWidth)),
    Math.max(0, Math.min(top, viewHeight - tooltipHeight)),
  ];
}
